///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Single source of truth for anchored React info panel sizes, shared by
/// the engine (FocusedObjectCallout placement/clamping) and the React
/// shell. Heights are placement estimates, not hard CSS constraints:
/// under-reporting makes the engine pick spots (especially "below")
/// where the real content runs off-screen.
///
/// Compact sizes are reported on mobile, where the panels render a reduced
/// layout with the detail sections behind a "More" expander. They are
/// nominal only: mobile React panels render as a bottom sheet
/// (AnchoredPanelShell) and the engine skips placement for them.
///

import { isMobile } from "@/earthbrowser/utils/platform"

export interface AnchoredPanelSize {
	width: number
	height: number
}

interface PanelSizeEntry {
	desktop: AnchoredPanelSize
	compact: AnchoredPanelSize
}

const cReactPanelSizes: Record<string, PanelSizeEntry> = {
	location: { desktop: { width: 300, height: 356 }, compact: { width: 280, height: 210 } },
	volcano: { desktop: { width: 320, height: 260 }, compact: { width: 280, height: 140 } },
	satellite: { desktop: { width: 320, height: 280 }, compact: { width: 280, height: 200 } },
	earthquake: { desktop: { width: 320, height: 280 }, compact: { width: 280, height: 150 } },
	// Buoy reserves the tallest common layout (met hero + sea state +
	// 3-day sparklines + station meta) so "below" placement fits
	buoy: { desktop: { width: 320, height: 480 }, compact: { width: 280, height: 200 } },
	// Webcam: header + 16:9 live image + attribution footer
	webcam: { desktop: { width: 340, height: 330 }, compact: { width: 300, height: 270 } },
	wildfire: { desktop: { width: 320, height: 300 }, compact: { width: 280, height: 200 } },
	hurricane: { desktop: { width: 320, height: 300 }, compact: { width: 280, height: 180 } },
	// Alert: severity badge + expiry hero + headline; description scrolls in More
	alert: { desktop: { width: 340, height: 320 }, compact: { width: 300, height: 200 } },
	// Placemark: coordinates + alert roll-up + conditions row; member list in More
	placemark: { desktop: { width: 320, height: 360 }, compact: { width: 280, height: 200 } },
}

/** Content types that render as anchored React panels when focused */
export const cReactPanelContentTypes: ReadonlySet<string> = new Set(Object.keys(cReactPanelSizes))

/** Placement size for a React info panel, compact on mobile devices */
export function reactPanelSize(contentType: string | null | undefined): AnchoredPanelSize | null {
	if (!contentType) return null
	const entry = cReactPanelSizes[contentType]
	if (!entry) return null
	return isMobile() ? entry.compact : entry.desktop
}
